import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpService } from './http.service';
import {
  DEFAULT_E_LINKS_CURRENT_PAGE,
  DEFAULT_E_LINKS_PER_PAGE,
  DEFAULT_E_LINKS_SORT,
  DEFAULT_E_LINKS_SORT_DIRECTION,
  ELink,
  ELinkApiSetting,
  ELinkCreateLinkPayload,
  ELinkCreatedLink,
  ELinkDetail,
  ELinkExternalDomain,
  ELinkExternalPage,
  ELinkExternalTemplate,
  ELinkManageOptions,
  ELinkSavePayload,
  ELinkSelectOption,
  ELinkTemplateDetail,
  ELinksListMeta,
  ELinksListParams,
  ELinksListResponse,
} from '../../store/entities/models/e-link.model';
import { transformSnakeToCamel } from '../../store/utils/transformers';

/**
 * HTTP layer for the E-Link (E-Link Pages / Templates) feature.
 *
 * Mirrors the React `services/ELink/ELink.js` service. Unlike Power Pages,
 * the E-Link endpoints are NOT domain-scoped — they live under `/elink-pages`.
 * The backend returns snake_case, so responses run through
 * `transformSnakeToCamel` at this boundary.
 */
@Injectable({
  providedIn: 'root',
})
export class ELinksService {
  private http = inject(HttpService);

  private static readonly BASE = '/elink-pages';

  /**
   * GET /elink-pages
   *
   * The React `BE_FE_MAP.getAll` maps the paginated response so that:
   *   - each row's `name` becomes `title`, `thumbnail_logo.value` becomes
   *     `imageUrl`, `thumbnail_logo.is_enabled` becomes `isImageEnabled`, etc.
   *   - the `pagination` block carries `current_page`/`per_page`/`last_page`.
   *
   * We rely on the generic snake→camel transform and then normalise the
   * fields the list page needs.
   */
  getELinksList(params: ELinksListParams = {}): Observable<ELinksListResponse> {
    const queryParams: Record<string, unknown> = {
      page: params.page ?? DEFAULT_E_LINKS_CURRENT_PAGE,
      per_page: params.perPage ?? DEFAULT_E_LINKS_PER_PAGE,
      search: params.search ?? '',
      sort: params.sort ?? DEFAULT_E_LINKS_SORT,
      sort_direction: params.sortDirection ?? DEFAULT_E_LINKS_SORT_DIRECTION,
    };

    return this.http
      .get<Record<string, unknown>>(ELinksService.BASE, queryParams)
      .pipe(map((response) => this.normaliseListResponse(response)));
  }

  /** DELETE /elink-pages/:uuid */
  deleteELink(uuid: string): Observable<void> {
    return this.http.delete<void>(`${ELinksService.BASE}/${uuid}`);
  }

  /* ------------------------------------------------------------------------ */
  /* Editor (create/edit)                                                      */
  /* ------------------------------------------------------------------------ */

  /**
   * GET /elink-pages/:uuid
   *
   * Mirrors React `getOne`: flattens the option arrays to arrays of uuid
   * strings, the redirect type to its `value`, and reduces the `settings`
   * array into a map keyed by the numeric setting code.
   */
  getELinkById(uuid: string): Observable<ELinkDetail> {
    return this.http
      .get<Record<string, unknown>>(`${ELinksService.BASE}/${uuid}`)
      .pipe(map((response) => this.normaliseDetail(response)));
  }

  /**
   * Loads the editor dropdown options (domains, link templates, qr templates),
   * mirroring the React `internalFieldOptionsSelector`. Each list comes from a
   * separate endpoint; here we fetch them in parallel.
   */
  getELinkOptions(): Observable<ELinkManageOptions> {
    return forkJoin({
      domains: this.http.get<Record<string, unknown>>('/profile/domains', { per_page: 10000 }),
      linkTemplates: this.http.get<Record<string, unknown>>('/templates', { per_page: 10000 }),
      qrTemplates: this.http.get<Record<string, unknown>>('/qr/templates', { per_page: 10000 }),
    }).pipe(
      map(({ domains, linkTemplates, qrTemplates }) => ({
        domains: this.toOptions(domains, 'name'),
        linkTemplates: this.toOptions(linkTemplates, 'templateName', 'name').sort((left, right) =>
          left.label.localeCompare(right.label)
        ),
        qrTemplates: this.toOptions(qrTemplates, 'name'),
      }))
    );
  }

  /** POST /elink-pages */
  createELink(payload: ELinkSavePayload): Observable<ELinkDetail> {
    return this.http
      .post<Record<string, unknown>>(ELinksService.BASE, this.toApiPayload(payload))
      .pipe(map((response) => this.extractSaved(response)));
  }

  /** PUT /elink-pages/:uuid */
  updateELink(uuid: string, payload: ELinkSavePayload): Observable<ELinkDetail> {
    return this.http
      .put<Record<string, unknown>>(`${ELinksService.BASE}/${uuid}`, this.toApiPayload(payload))
      .pipe(map((response) => this.extractSaved(response)));
  }

  /** PATCH /elink-pages/:uuid/publish */
  publishELink(uuid: string): Observable<ELinkDetail> {
    return this.http
      .patch<Record<string, unknown>>(`${ELinksService.BASE}/${uuid}/publish`)
      .pipe(map((response) => this.extractSaved(response)));
  }

  /** PATCH /elink-pages/:uuid/unpublish */
  unpublishELink(uuid: string): Observable<ELinkDetail> {
    return this.http
      .patch<Record<string, unknown>>(`${ELinksService.BASE}/${uuid}/unpublish`)
      .pipe(map((response) => this.extractSaved(response)));
  }

  /** DELETE /elink-page-settings/:uuid (removes an uploaded image setting). */
  deleteELinkSetting(uuid: string): Observable<void> {
    return this.http.delete<void>(`/elink-page-settings/${uuid}`);
  }

  /* ------------------------------------------------------------------------ */
  /* External (public) page                                                    */
  /* ------------------------------------------------------------------------ */

  /**
   * GET /external/elink-pages/:slug — the public page config an end user sees.
   * Reuses the detail normaliser but keeps the option arrays as `{label,value}`
   * objects (the public creation form needs the labels/ids), and reduces
   * settings into a code-keyed map.
   */
  getExternalPage(slug: string): Observable<ELinkExternalPage> {
    return this.http
      .get<Record<string, unknown>>(`/external/elink-pages/${slug}`)
      .pipe(map((response) => this.normaliseExternalPage(response)));
  }

  /** GET /external/elink-pages/:pageUuid/link-templates/:templateUuid */
  getExternalLinkTemplate(
    pageUuid: string,
    templateUuid: string
  ): Observable<ELinkTemplateDetail> {
    return this.http
      .get<Record<string, unknown>>(
        `/external/elink-pages/${pageUuid}/link-templates/${templateUuid}`
      )
      .pipe(
        map((response) => {
          const transformed = transformSnakeToCamel(response) as Record<string, unknown>;
          const data = (transformed.data ?? transformed) as Record<string, unknown>;
          const attributes = Array.isArray(data.assignedAttributes)
            ? (data.assignedAttributes as Record<string, unknown>[])
            : [];

          return {
            name: data.name as string | undefined,
            redirectType: data.redirectType as string | undefined,
            additionalParameterSchema: data.additionalParameterSchema,
            slugPrefix: (data.slugPrefix as string | undefined) ?? '',
            assignedAttributes: attributes.map((attr) => ({
              uuid: String(attr.uuid ?? ''),
              code: String(attr.code ?? ''),
              name: String(attr.name ?? ''),
              description: attr.description as string | undefined,
              dataType: attr.dataType as string | undefined,
              fieldType: attr.fieldType as string | undefined,
              defaultValue: (attr.defaultValue as string | undefined) ?? '',
              type: attr.type as string | undefined,
              isRequiredTemplate: Boolean(attr.isRequiredTemplate),
            })),
          } as ELinkTemplateDetail;
        })
      );
  }

  /**
   * POST /external/elink-pages/:uuid/links/slug — generates a suggested slug.
   * Returns the raw slug string.
   */
  getExternalSlug(uuid: string, params: { domain: string; template?: string }): Observable<string> {
    return this.http
      .post<Record<string, unknown>>(`/external/elink-pages/${uuid}/links/slug`, {
        domain_uuid: params.domain,
        template_uuid: params.template,
      })
      .pipe(map((response) => String((response as { slug?: string }).slug ?? '')));
  }

  /** POST /external/elink-pages/:uuid/links/validate-slug — returns validity. */
  validateExternalSlug(
    uuid: string,
    params: { domain: string; slug: string }
  ): Observable<boolean> {
    return this.http
      .post<Record<string, unknown>>(`/external/elink-pages/${uuid}/links/validate-slug`, {
        domain_uuid: params.domain,
        slug: params.slug,
      })
      .pipe(map((response) => Boolean((response as { is_valid?: boolean }).is_valid)));
  }

  /** POST /external/elink-pages/:uuid/links — creates the end-user link. */
  createExternalLink(payload: ELinkCreateLinkPayload): Observable<ELinkCreatedLink> {
    const body: Record<string, unknown> = {
      domain_uuid: payload.domain,
      template_uuid: payload.template,
      long_url: payload.longUrl,
      alias: payload.slug,
      notes: payload.description,
      redirect_type: payload.redirectType,
      additional_parameter_schema: payload.additionalParameterSchema,
      assigned_attributes: payload.attributes,
    };

    return this.http
      .post<Record<string, unknown>>(`/external/elink-pages/${payload.uuid}/links`, body)
      .pipe(
        map((response) => {
          const transformed = transformSnakeToCamel(response) as Record<string, unknown>;
          const data = (transformed.data ?? {}) as Record<string, unknown>;
          const meta = (transformed.meta ?? {}) as Record<string, unknown>;
          const merged = { ...data, ...meta };

          return {
            shortURL: String(merged.shortUrl ?? merged.shortURL ?? ''),
            domain: merged.domain as { name?: string; ssl?: boolean } | undefined,
            alias: merged.alias as string | undefined,
            isExistedLink: Boolean(merged.isExistingLink ?? merged.isExistedLink),
          };
        })
      );
  }

  /**
   * Normalises the public page response. Keeps option arrays as label/value
   * objects and reduces the settings array into a code-keyed map.
   */
  private normaliseExternalPage(response: Record<string, unknown>): ELinkExternalPage {
    const transformed = transformSnakeToCamel(response) as Record<string, unknown>;
    const data = (transformed.data ?? transformed) as Record<string, unknown>;

    const settingsArray = Array.isArray(data.settings)
      ? (data.settings as Record<string, unknown>[])
      : [];
    const settings: Record<string, ELinkApiSetting> = {};

    settingsArray.forEach((entry) => {
      const rawName = entry.name;
      const code =
        rawName && typeof rawName === 'object'
          ? String((rawName as Record<string, unknown>).value ?? '')
          : String(rawName ?? '');

      if (!code) {
        return;
      }

      settings[code] = {
        name: code,
        uuid: entry.uuid != null ? String(entry.uuid) : undefined,
        value: String(entry.value ?? ''),
        isEnabled: Boolean(entry.isEnabled),
      };
    });

    const mapDomains = (raw: unknown): ELinkExternalDomain[] =>
      Array.isArray(raw)
        ? (raw as Record<string, unknown>[]).map((item) => ({
            id: (item.id as string | number | undefined) ?? undefined,
            label: String(item.label ?? item.name ?? ''),
            value: String(item.value ?? item.uuid ?? ''),
            isTrustedEnabled: Boolean(item.isTrustedEnabled ?? item.isTrustedDestinations),
          }))
        : [];

    const mapTemplates = (raw: unknown): ELinkExternalTemplate[] =>
      Array.isArray(raw)
        ? (raw as Record<string, unknown>[]).map((item) => ({
            label: String(item.label ?? item.templateName ?? item.name ?? ''),
            value: String(item.value ?? item.uuid ?? ''),
          }))
        : [];

    return {
      uuid: String(data.uuid ?? ''),
      name: data.name as string | undefined,
      slug: data.slug as string | undefined,
      isQRCodeEnabled: Boolean(data.isQrCode ?? data.isQRCodeEnabled),
      isTrustedDestinationEnabled: Boolean(data.isTrustedDestination ?? data.isTrustedDestinationEnabled),
      isShortUrlEditable: data.isShortUrlEditable != null ? Boolean(data.isShortUrlEditable) : true,
      redirectType: (data.redirectType as { value?: string; label?: string } | string) ?? '',
      hiddenFields: this.toStringArray(data.hiddenColumns ?? data.hiddenFields),
      domains: mapDomains(data.domains),
      linkTemplates: mapTemplates(data.linkTemplates),
      qrTemplates: mapTemplates(data.qrTemplates),
      settings,
    };
  }

  /**
   * Maps the camelCase save payload to the snake_case API body. Mirrors the
   * React `FE_BE_MAP.createTemplate`: option arrays are wrapped as `[{ uuid }]`
   * and settings keep their `{ name, value, is_enabled }` shape.
   */
  private toApiPayload(payload: ELinkSavePayload): Record<string, unknown> {
    return {
      name: payload.name,
      description: payload.description ?? '',
      slug: payload.slug,
      redirect_type: payload.redirectType,
      is_qr_code: payload.isQRCodeEnabled,
      is_trusted_destination: payload.isTrustedDestinationEnabled ?? false,
      is_short_url_editable: payload.isShortUrlEditable,
      is_protected: payload.isProtected,
      is_use_existing_link: payload.isUseExistingLink,
      hidden_columns: payload.hiddenFields ?? [],
      domains: (payload.domains ?? []).map((uuid) => ({ uuid })),
      link_templates: (payload.linkTemplates ?? []).map((uuid) => ({ uuid })),
      qr_templates: (payload.qrTemplates ?? []).map((uuid) => ({ uuid })),
      settings: (payload.settings ?? []).map((setting) => ({
        name: setting.name,
        value: setting.value,
        is_enabled: setting.isEnabled,
      })),
    };
  }

  /**
   * Normalises a `getOne`-style detail response. Flattens option arrays to
   * uuid strings, the redirect type to its value, and reduces `settings` into
   * a map keyed by the numeric setting code.
   */
  private normaliseDetail(response: Record<string, unknown>): ELinkDetail {
    const transformed = transformSnakeToCamel(response) as Record<string, unknown>;
    const data = (transformed.data ?? transformed) as Record<string, unknown>;

    const redirect = data.redirectType as Record<string, unknown> | string | undefined;
    const redirectType =
      redirect && typeof redirect === 'object'
        ? String((redirect as Record<string, unknown>).value ?? '')
        : String(redirect ?? '');

    const settingsArray = Array.isArray(data.settings)
      ? (data.settings as Record<string, unknown>[])
      : [];
    const settings: Record<string, ELinkApiSetting> = {};

    settingsArray.forEach((entry) => {
      // The backend `name` may be nested (`name.value`) or a plain code.
      const rawName = entry.name;
      const code =
        rawName && typeof rawName === 'object'
          ? String((rawName as Record<string, unknown>).value ?? '')
          : String(rawName ?? '');

      if (!code) {
        return;
      }

      settings[code] = {
        name: code,
        uuid: entry.uuid != null ? String(entry.uuid) : undefined,
        value: String(entry.value ?? ''),
        isEnabled: Boolean(entry.isEnabled),
      };
    });

    return {
      uuid: data.uuid != null ? String(data.uuid) : undefined,
      name: (data.name as string) ?? '',
      description: (data.description as string | null) ?? '',
      slug: (data.slug as string | null) ?? '',
      isDefault: Boolean(data.isDefault),
      isQRCodeEnabled: Boolean(data.isQRCodeEnabled),
      isTrustedDestinationEnabled: Boolean(data.isTrustedDestinationEnabled),
      isShortUrlEditable: Boolean(data.isShortUrlEditable),
      isProtected: Boolean(data.isProtected),
      isPublished: (data.isPublished as boolean | string | null) ?? null,
      isUseExistingLink: Boolean(data.isUseExistingLink),
      redirectType,
      hiddenFields: this.toStringArray(data.hiddenFields),
      domains: this.toValueArray(data.domains),
      linkTemplates: this.toValueArray(data.linkTemplates),
      qrTemplates: this.toValueArray(data.qrTemplates),
      settings,
    };
  }

  /** Extracts the saved entity from a create/update/publish response. */
  private extractSaved(response: Record<string, unknown>): ELinkDetail {
    return this.normaliseDetail(response);
  }

  /**
   * Maps a list-style response (`{ data: [...] }`) to select options. Reads the
   * label from the first present key in `labelKeys` and the value from `uuid`.
   */
  private toOptions(response: Record<string, unknown>, ...labelKeys: string[]): ELinkSelectOption[] {
    const transformed = transformSnakeToCamel(response) as Record<string, unknown>;
    const rows = Array.isArray(transformed.data)
      ? (transformed.data as Record<string, unknown>[])
      : [];

    return rows.map((row) => {
      const label = labelKeys.map((key) => row[key]).find((value) => value != null);

      return {
        label: String(label ?? ''),
        value: String(row.uuid ?? ''),
      };
    });
  }

  /** Flattens an array of `{ value }`/`{ uuid }` option objects to uuid strings. */
  private toValueArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => {
        if (item && typeof item === 'object') {
          const record = item as Record<string, unknown>;

          return String(record.value ?? record.uuid ?? '');
        }

        return String(item ?? '');
      })
      .filter(Boolean);
  }

  private toStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((item) => String(item ?? '')).filter(Boolean);
  }

  /**
   * Normalises the raw list response into `{ data, meta }`. The backend nests
   * pagination under a `pagination` key (or, in some responses, `meta`); each
   * row uses `name` for its title and `thumbnail_logo` for the thumbnail. This
   * maps those onto the camelCase `ELink` shape the UI consumes.
   */
  private normaliseListResponse(response: Record<string, unknown>): ELinksListResponse {
    const transformed = transformSnakeToCamel(response) as Record<string, unknown>;

    const rawData = Array.isArray(transformed.data) ? (transformed.data as Record<string, unknown>[]) : [];
    const rawMeta = (transformed.pagination ?? transformed.meta ?? {}) as Record<string, unknown>;

    const data: ELink[] = rawData.map((row) => this.mapRow(row));
    const meta: ELinksListMeta = {
      currentPage: this.toNumber(rawMeta.currentPage, DEFAULT_E_LINKS_CURRENT_PAGE),
      perPage: this.toNumber(rawMeta.perPage, DEFAULT_E_LINKS_PER_PAGE),
      total: rawMeta.total != null ? this.toNumber(rawMeta.total, 0) : undefined,
      totalPages: rawMeta.totalPages != null ? this.toNumber(rawMeta.totalPages, undefined) : undefined,
    };

    return { data, meta };
  }

  /** Maps a single transformed row onto the `ELink` view model. */
  private mapRow(row: Record<string, unknown>): ELink {
    const thumbnail = (row.thumbnailLogo ?? {}) as Record<string, unknown>;

    return {
      uuid: String(row.uuid ?? ''),
      // React maps backend `name` -> `title`.
      title: String(row.name ?? row.title ?? ''),
      description: (row.description as string | null) ?? null,
      slug: (row.slug as string | null) ?? null,
      isDefault: Boolean(row.isDefault),
      shortUrl: (row.shortUrl as string | null) ?? null,
      longUrl: (row.longUrl as string | null) ?? null,
      imageUrl: (thumbnail.value as string | null) ?? null,
      isImageEnabled: Boolean(thumbnail.isEnabled),
    };
  }

  private toNumber(value: unknown, fallback: number): number;
  private toNumber(value: unknown, fallback: undefined): number | undefined;
  private toNumber(value: unknown, fallback: number | undefined): number | undefined {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : fallback;
  }
}
